import { MenuItem, TextField, Typography, Box } from '@mui/material'
import {
  ISelectInput,
  ExtendedOptionObjectPair,
  ExtendedOption,
} from './SelectInput.types'
import { makeStyles } from '@material-ui/core/styles'
import { OptionObjectPair, Option } from 'common/types'
import { useEffect, useState, forwardRef } from 'react'

const SelectInput = forwardRef<HTMLDivElement, ISelectInput>(
  (
    {
      id,
      label,
      name,
      disabled = false,
      options,
      placeholder = 'Select',
      required = false,
      value = '',
      itemStyles = {},
      sx = {},
      errorMessage,
      shrink = true,
      autoFocus = false,
      variant = 'standard',
      handleChangeFormData = () => {},
      ...rest
    },
    ref
  ) => {
    const useStyles = makeStyles({
      select: {
        '& ul': {
          backgroundColor: 'white',
        },
        '& li': {
          position: 'relative',
          zIndex: 1,
        },
      },
    })

    const classes = useStyles()
    const [open, setOpen] = useState(false)

    useEffect(() => {
      if (autoFocus) {
        const timer = setTimeout(() => {
          setOpen(true)
        }, 100)
        return () => clearTimeout(timer)
      } else {
        setOpen(false)
      }
    }, [autoFocus])

    return (
      <div className="select-input-container" ref={ref}>
        <TextField
          autoFocus={autoFocus}
          select
          fullWidth
          disabled={disabled}
          required={required}
          id={id}
          label={label}
          classes={{
            root: `select {
              background-color: "red"
            }`,
          }}
          name={name}
          value={value}
          onChange={handleChangeFormData}
          SelectProps={{
            open,
            onClose: () => setOpen(false),
            onOpen: () => setOpen(true),
            displayEmpty: true,
            renderValue: value
              ? (selectedValue) => {
                  const selectedOption = options.find(
                    (opt) =>
                      ((opt as OptionObjectPair).value ||
                        (opt as Option).id) === selectedValue
                  )

                  const optionDotColor = (
                    selectedOption as ExtendedOptionObjectPair | ExtendedOption
                  )?.dotColor

                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {optionDotColor && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor:
                              optionDotColor === 'gray' ? '#898989' : '#4CAF50',
                            marginRight: 1,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {(selectedOption as OptionObjectPair)?.label ||
                        (selectedOption as Option)?.name}
                    </Box>
                  )
                }
              : () => <Typography color={'gray'}>{placeholder}</Typography>,
            MenuProps: {
              classes: { paper: classes.select },
            },
          }}
          sx={{
            my: 2,
            textTransform: 'capitalize',
            ...sx,
          }}
          variant={variant}
          InputLabelProps={{
            shrink: value ? true : shrink,
          }}
          {...rest}
        >
          {options.map((option, i) => {
            const extendedOption = option as
              | ExtendedOptionObjectPair
              | ExtendedOption
            const optionDotColor = extendedOption?.dotColor
            const isDisabled = extendedOption?.disabled

            return (
              <MenuItem
                autoFocus={autoFocus}
                key={i}
                value={
                  (option as OptionObjectPair).value
                    ? (option as OptionObjectPair).value
                    : (option as Option).id
                }
                disabled={isDisabled}
                style={
                  isDisabled
                    ? {
                        opacity: 1,
                        color: '#818181',
                        backgroundColor: '#f5f5f5',
                        cursor: 'not-allowed',
                        fontStyle: 'italic',
                        pointerEvents: 'none',
                      }
                    : {}
                }
                sx={{
                  textTransform: 'capitalize',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 'auto',
                  ...itemStyles,
                }}
              >
                {optionDotColor && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor:
                        optionDotColor === 'gray' ? '#9E9E9E' : '#4CAF50',
                      marginRight: 1,
                      flexShrink: 0,
                      ...(isDisabled && {
                        opacity: 0.3,
                        backgroundColor: '#d0d0d0 !important',
                        border: '1px solid #bdbdbd',
                      }),
                    }}
                  />
                )}
                {(option as OptionObjectPair).label
                  ? (option as OptionObjectPair).label
                  : (option as Option).name}
              </MenuItem>
            )
          })}
        </TextField>
        {errorMessage && (
          <Typography fontSize={12} color="red" ml={0.5} mt={-1}>
            {errorMessage}
          </Typography>
        )}
      </div>
    )
  }
)

export default SelectInput
